/**
 * `@repo/theme/a11y/contrast` — the ONE brand-colour contrast fallback (features/accessibility.md R2).
 *
 * Pure, dependency-free (no Panda, no DOM) so the Astro venue-site build can import it without pulling
 * the design-system toolchain into its per-venue seconds budget, and the widget can call it at theme
 * resolution on mount. Both surfaces import THIS module — byte-identical behaviour by construction.
 *
 *  - `contrastRatio(a, b)`  — WCAG 2.1 relative-luminance ratio (1..21) between two hex colours.
 *  - `ensureContrast(brand, surface, target)` — return a shade of `brand`, hue-preserving, that clears
 *    `target` against `surface` (≥4.5 body text, ≥3 UI/large text). The search runs on OKLCH LIGHTNESS
 *    only (chroma + hue held), so the adjusted shade stays recognisably the venue's colour; it is PURE
 *    and DETERMINISTIC, and an already-passing input is returned UNCHANGED (never re-normalised).
 *
 * The stored brand colour is NEVER mutated — callers keep the raw value for the logo + decorative fills
 * and use the returned shade only for text/interactive tokens (see apps/venue-sites, apps/widget).
 */

/** WCAG contrast target constants (2.1 AA, ADR-0023). */
export const CONTRAST_AA_TEXT = 4.5;
export const CONTRAST_AA_LARGE = 3;

/** Parse `#rgb` / `#rrggbb` (with or without `#`) into [r,g,b] 0..255, or null when malformed. */
export function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  const captured = m?.[1];
  if (!captured) return null;
  const h =
    captured.length === 3
      ? captured
          .split("")
          .map((c) => c + c)
          .join("")
      : captured;
  const n = Number.parseInt(h, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

const clampByte = (c: number) => Math.max(0, Math.min(255, Math.round(c)));

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((c) => clampByte(c).toString(16).padStart(2, "0")).join("")}`;
}

function channelLuminance(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG 2.1 relative luminance of a hex colour (0..1); a malformed value is treated as black. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex) ?? [0, 0, 0];
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

/** WCAG 2.1 contrast ratio between two hex colours (1..21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// --- sRGB ⇄ OKLab ⇄ OKLCH (Björn Ottosson). Pure math; operates on 0..1 gamma-encoded sRGB. -------

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function linearToSrgb(c: number): number {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
  return Math.max(0, Math.min(1, v));
}

interface Oklch {
  L: number;
  C: number;
  h: number;
}

function rgbToOklch([r255, g255, b255]: [number, number, number]): Oklch {
  const r = srgbToLinear(r255 / 255);
  const g = srgbToLinear(g255 / 255);
  const b = srgbToLinear(b255 / 255);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { L, C: Math.hypot(a, bb), h: Math.atan2(bb, a) };
}

function oklchToRgb({ L, C, h }: Oklch): [number, number, number] {
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);
  const lCube = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mCube = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const sCube = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const r = 4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube;
  const g = -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube;
  const bl = -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube;
  return [linearToSrgb(r) * 255, linearToSrgb(g) * 255, linearToSrgb(bl) * 255];
}

/** The hex shade at OKLCH lightness `L` (chroma + hue held), gamut-clipped per channel. */
function shadeAtLightness(base: Oklch, L: number): string {
  return toHex(oklchToRgb({ L, C: base.C, h: base.h }));
}

/**
 * Return a shade of `brand` (OKLCH hue + chroma preserved) that meets `target` contrast against
 * `surface`. If `brand` already clears `target`, it is returned unchanged. Otherwise the search moves
 * lightness toward whichever pole (black or white) contrasts more with `surface` and binary-searches
 * for the shade CLOSEST to the original that still passes — minimal, hue-faithful adjustment.
 *
 * Malformed input (not a hex colour) is returned unchanged: the OKLCH search is undefined for it, and
 * the caller's downstream allowlist/fallback owns non-hex values.
 */
export function ensureContrast(brand: string, surface: string, target: number): string {
  const rgb = parseHex(brand);
  if (!rgb) return brand;
  if (contrastRatio(brand, surface) >= target) return brand;

  const base = rgbToOklch(rgb);

  // Move toward the pole that contrasts MORE with the surface (light surface → darken; dark → lighten).
  const darken = contrastRatio(surface, "#000000") >= contrastRatio(surface, "#ffffff");
  let lo = darken ? 0 : base.L;
  let hi = darken ? base.L : 1;

  // If even the pole cannot reach the target against this surface, return the pole shade (best effort).
  const poleL = darken ? 0 : 1;
  if (contrastRatio(shadeAtLightness(base, poleL), surface) < target) {
    return shadeAtLightness(base, poleL);
  }

  // Contrast is monotonic in L on each side of the surface; find the lightness closest to the original
  // (max L when darkening, min L when lightening) that still clears the target.
  let best = shadeAtLightness(base, poleL);
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    const shade = shadeAtLightness(base, mid);
    if (contrastRatio(shade, surface) >= target) {
      best = shade;
      if (darken) lo = mid;
      else hi = mid;
    } else if (darken) {
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return best;
}
